import { StateSetter } from 'react';
import { useLang } from 'hooks/lang';

import { TeamService } from 'entities';

import { Tag, Column, Row, TitledCard, Dot, IconButton } from 'components/Blocks';

import { BadgeDollarIcon, BinaryIcon, PencilIcon } from 'components/Icons';

import { booleanRenders, FormTeamService } from './Form';

import { ServiceStatus } from './Status';
import { createExpression } from './calculations';

import { scrollToWrapperTop } from 'utils/html';

import { useTeamProfile } from '../context';

export function ServiceCards({
  setFocusedService,
}: {
  setFocusedService: StateSetter<FormTeamService | null>;
}) {
  const { team, canMutateEntities } = useTeamProfile();

  const { t } = useLang();

  return (
    <Column gap="6">
      {team.services.map((service) => (
        <TitledCard
          title={service.name}
          key={service.name}
          titleClassName="text-xl"
          headerRightContent={
            <Row gap="2">
              <ServiceStatus service={service} />

              {canMutateEntities && (
                <IconButton
                  className="text-primary"
                  tooltipContent={t('update')}
                  onClick={() => {
                    setFocusedService(service);
                    scrollToWrapperTop();
                  }}
                >
                  <PencilIcon />
                </IconButton>
              )}
            </Row>
          }
        >
          <Column gap="8">
            {!!service.tags.length && (
              <Row flexWrap="wrap" gap="2">
                {service.tags.map((tag) => (
                  <Tag
                    key={`service-${service.name}-tag-${tag.name}`}
                    name={tag.name}
                    color={tag.color}
                    className="text-white"
                    size="medium"
                  />
                ))}
              </Row>
            )}

            <Column>
              <strong>{t('variables')}:</strong>

              {service.costVariables?.map((cost) => (
                <Row className="pl-2.5" key={`service-${service.name}-cost-${cost.name}`} gap="2">
                  {cost.type === 'CURRENCY' ? <BadgeDollarIcon /> : <BinaryIcon />}
                  <span> {cost.name}</span>
                </Row>
              ))}
            </Column>

            <Row>
              <strong>{t('cost-calculation')}:</strong>

              <span>{createExpression(service)}</span>
            </Row>

            {!!booleanRenders.filter(({ property }) => service[property as keyof TeamService])
              .length && (
              <Column>
                <strong>{t('extensions')}:</strong>

                {booleanRenders
                  .filter(({ property }) => service[property as keyof TeamService])
                  .map(({ getLabel, property }) => (
                    <Row className="pl-2.5" gap="2" key={`service-${service.id}-bool-${property}`}>
                      <Dot color="muted-foreground" size={4} />

                      <span> {getLabel(t)}</span>
                    </Row>
                  ))}
              </Column>
            )}

            {!!service.fileNames?.length && (
              <Column>
                <strong>{t('file-names')}:</strong>

                {service.fileNames.map((name) => (
                  <Row className="pl-2.5" gap="2" key={name}>
                    <Dot color="muted-foreground" size={4} />

                    <span> {name}</span>
                  </Row>
                ))}
              </Column>
            )}
          </Column>
        </TitledCard>
      ))}
    </Column>
  );
}
